import React, { useState, useEffect } from "react";
import { Card, CardBody, Container, Button, Spinner } from "reactstrap";
import ActiveInActive, {
  ActiveInActivePayload,
} from "common/utilities/Active-InActive/Active-InActive";
import {
  perPageData,
  showToastMessage,
} from "../../../../common/service/CommonService";
import tableStyles from "../../../../assets/scss/common/table.module.scss";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import { PeriodFormValues, PeriodRecord } from "../helper/period.types";
import { useMasterPeriodService } from "../api/usePeriodService";
import Reason, {
  ReasonSubmitPayload,
} from "../../../../common/utilities/Reason/Reason";
import CommonTableIndexWise from "../../../../Components/Common/CommonTableIndexWise";
import { periodColumn } from "../helper/periodconstant";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import CommonButton from "../../../../Components/Common/CommonButton";
import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
} from "../../../../common/constants/PermissionConstants";
import AddEditPeriod from "../components/AddEditPeriod";
import { queryClient } from "api/queryClient";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import { useAuditService } from "modules/Audit/api/useAuditQuery";

const PeriodList: React.FC = () => {
  document.title = "Period | ZENTIXS VISTA";
  const [searchText, setSearchText] = useState<string>("");
  const [showAddEdit, setShowAddEdit] = useState<boolean>(false);
  const [editRecord, setEditRecord] = useState<PeriodRecord | null>(null);
  const [editId, setEditId] = useState<number | undefined>(undefined);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<PeriodRecord | null>(
    null,
  );
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [updatedRecord, setUpdatedRecord] = useState<PeriodFormValues | null>(
    null,
  );
  const [isReasonModal, setIsReasonModal] = useState<boolean>(false);
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);

  //Audit
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const { SelectAllMutation: AuditMutation } = useAuditService();

  const {
    SelectAllQuery,
    SelectQuery,
    InsertMutation,
    UpdateMutation,
    DeleteMutation,
  } = useMasterPeriodService(
    searchText,
    editId,
    currentPageNumber,
    perPageData,
  );

  // Populate edit form when SelectQuery returns data
  useEffect(() => {
    if (SelectQuery?.data && editId) {
      const selectData =
        (SelectQuery as any).data?.data?.[0] ?? (SelectQuery as any).data;
      if (selectData) {
        const formValues: PeriodRecord = {
          masterperiodid: selectData.masterperiodid,
          period_sort: selectData.period_sort,
          period_number: selectData.period_number,
        };
        setEditRecord(formValues);
        setShowAddEdit(true);
      }
    }
  }, [SelectQuery?.data, editId]);

  const toggleAddEditPeriod = (): void => {
    if (showAddEdit) {
      setEditId(undefined);
      setEditRecord(null);
    }
    setShowAddEdit(!showAddEdit);
  };

  const toggleReasonModal = (): void => setIsReasonModal((prev) => !prev);
  const toggleDeleteModal = (): void => {
    setDeleteModal(!deleteModal);
    setShowDeleteReason(false);
  };
  const toggleDeleteReason = (): void => setShowDeleteReason(!showDeleteReason);

  const {
    data: queryResponse,
    isLoading,
    isFetching,
  } = SelectAllQuery as {
    data?: { data?: (PeriodRecord & { total_count: number })[] };
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
  };

  const data: (PeriodRecord & { total_count: number })[] =
    queryResponse?.data ?? [];

  const refreshTableData = () => {
    if (currentPageNumber === 1) {
      SelectAllQuery.refetch?.();
    } else {
      setCurrentPageNumber(1);
    }
  };

  const onSearch = (text: string | null) => {
    setSearchText(text ?? "");
    setCurrentPageNumber(1);
  };

  const onAddMaster = (): void => {
    setEditRecord(null);
    setEditId(undefined);
    setShowAddEdit(true);
  };

  const onEditMaster = (item: PeriodRecord): void => {
    setEditId(item.masterperiodid);
  };

  const btnReasonClick = (reasonPayload: ReasonSubmitPayload) => {
    if (!editRecord || editRecord.masterperiodid == null || !updatedRecord)
      return;
    if (reasonPayload.id == null) return;

    const payload = {
      masterperiodid: editRecord.masterperiodid,
      period_sort: updatedRecord.period_sort,
      period_number: updatedRecord.period_number,
      updatereason: reasonPayload.reason,
    };

    UpdateMutation.mutate(payload, {
      onSuccess: (response) => {
        queryClient.invalidateQueries({
          queryKey: ["master-period-selectAll"],
        });
        setSearchText("")
        showToastMessage("success", response?.message);
        toggleReasonModal();
        toggleAddEditPeriod();
        setEditRecord(null);
      },
      onError: (error: any) => {
        showToastMessage("error", error?.message ?? error);
      },
    });
  };

  const onSaveMaster = (record: PeriodFormValues): void => {
    if (editRecord?.masterperiodid) {
      setUpdatedRecord(record);
      toggleReasonModal();
    } else {
      InsertMutation.mutate(record, {
        onSuccess: (response) => {
          refreshTableData();
          setSearchText("")
          showToastMessage("success", response?.message);
          toggleAddEditPeriod();
        },
        onError: (error: any) => {
          showToastMessage("error", error?.message ?? error);
        },
      });
    }
  };

  const onDelete = (item: PeriodRecord): void => {
    setSelectedRecord(item);
    setDeleteModal(true);
  };

  const confirmDelete = (reasonPayload: ReasonSubmitPayload): void => {
    if (!selectedRecord) return;
    if (reasonPayload.id == null) return;

    const payload = {
      masterperiodid: reasonPayload.id,
      updatereason: reasonPayload.reason,
    };

    DeleteMutation.mutate(payload, {
      onSuccess: (response) => {
        showToastMessage("success", response?.message);

        const totalRecords = data[0]?.total_count || 0;
        const lastPage = Math.ceil((totalRecords - 1) / perPageData);

        setCurrentPageNumber(
          Math.max(1, Math.min(currentPageNumber, lastPage)),
        );

        queryClient.invalidateQueries({
          queryKey: ["master-period-selectAll"],
        });

        toggleDeleteModal();
        setSelectedRecord(null);
        setSearchText("")
      },

      onError: (error: Error) => {
        showToastMessage("error", error?.message ?? error);
      },
    });
  };

  const confirmActive = (obj: ActiveInActivePayload): void => {
    showToastMessage("success", "Status updated!");
    setActiveModal(false);
  };

  const onAuditClick = () => {
    const payload = {
      modulename: "Period",
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Period_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  return (
    <div className="page-content">
      <Container
        fluid
        className="px-0 d-flex flex-column"
        style={{ minHeight: "calc(100vh - 140px)" }}
      >
        <Card className={`${tableStyles.card} flex-grow-1`}>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <CommonSearch onSearch={onSearch} searchText={searchText} />
              <div className="d-flex gap-2">
                <CommonButton
                  module={PERMISSION_MODULES.PERIOD}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  size="sm"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
                <CommonButton
                  color="primary"
                  size="sm"
                  onClick={onAddMaster}
                  outline
                  module={PERMISSION_MODULES.PERIOD}
                  action={PERMISSION_ACTIONS.ADD}
                >
                  <i className="ri-add-line me-1"></i> Add
                </CommonButton>
              </div>
            </div>
            <CommonTableIndexWise
              moduleName={PERMISSION_MODULES.PERIOD}
              columns={periodColumn}
              data={data}
              showStatus={false}
              actions={{
                onEdit: onEditMaster,
                onDelete,
              }}
              pageSize={perPageData}
              currentPage={currentPageNumber}
              onPageChange={setCurrentPageNumber}
              totalRecords={data[0]?.total_count || 0}
              loading={isLoading || isFetching}
            />
          </CardBody>
        </Card>

        {/* Add/Edit Modal */}
        <AddEditPeriod
          isOpen={showAddEdit}
          toggle={toggleAddEditPeriod}
          existingData={editRecord}
          onSave={onSaveMaster}
          isLoading={InsertMutation.isPending}
        />

        {/* Reason Modal */}
        {isReasonModal && editRecord?.masterperiodid != null && (
          <Reason
            isOpen={isReasonModal}
            toggle={toggleReasonModal}
            action="Update"
            id={editRecord.masterperiodid}
            click={btnReasonClick}
            isLoading={UpdateMutation.isPending}
          />
        )}

        {/* Delete Modal */}
        {deleteModal && (
          <DeleteModal
            isopen={deleteModal}
            toggle={toggleDeleteModal}
            recordId={selectedRecord?.masterperiodid ?? 0}
            recordName={String(selectedRecord?.period_sort)}
            onDeleteClick={confirmDelete}
            showDeleteReason={showDeleteReason}
            toggleDeleteReason={toggleDeleteReason}
            isLoading={DeleteMutation?.isPending}
          />
        )}

        <ActiveInActive
          isOpen={activeModal}
          toggle={() => setActiveModal(false)}
          status={selectedRecord?.isActive ? "I" : "A"}
          id={selectedRecord?.masterperiodid ?? 0}
          click={confirmActive}
        />
      </Container>

      {/* File Preview Modal */}
      {isPreviewOpen && (
        <FilePreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          size={"xl"}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewFile(null);
          }}
        />
      )}
    </div>
  );
};

export default PeriodList;
